import { useEffect, useCallback } from "react";

interface ShortcutActions {
  readonly onNavigateUp: () => void;
  readonly onNavigateDown: () => void;
  readonly onToggleSelect: () => void;
  readonly onSelectAll: () => void;
  readonly onClear: () => void;
  readonly onEdit: () => void;
  readonly onStockDrawer: () => void;
  readonly onCommandPalette: () => void;
  readonly onFocusSearch: () => void;
  readonly onShowShortcuts: () => void;
}

/**
 * Registers keyboard shortcuts for the products table.
 * Only active when no input/textarea/select is focused.
 * Cmd/Ctrl+K is allowed even inside inputs so the command palette is always reachable.
 */
export function useKeyboardShortcuts(actions: ShortcutActions): void {
  const handler = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      const isEditable =
        ["INPUT", "TEXTAREA", "SELECT"].includes(tag) ||
        target?.isContentEditable === true;

      // Cmd/Ctrl+K: command palette (always, even inside inputs)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        actions.onCommandPalette();
        return;
      }

      if (isEditable) {
        return;
      }

      // Cmd/Ctrl+F: focus search
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        actions.onFocusSearch();
        return;
      }

      // Skip remaining shortcuts when modifiers are held (except shift for select-all)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "ArrowUp":
        case "k":
          e.preventDefault();
          actions.onNavigateUp();
          break;
        case "ArrowDown":
        case "j":
          e.preventDefault();
          actions.onNavigateDown();
          break;
        case "x":
          actions.onToggleSelect();
          break;
        case "A":
          if (e.shiftKey) actions.onSelectAll();
          break;
        case "Escape":
          actions.onClear();
          break;
        case "e":
          actions.onEdit();
          break;
        case "s":
          actions.onStockDrawer();
          break;
        case "?":
          actions.onShowShortcuts();
          break;
      }
    },
    [actions],
  );

  useEffect(() => {
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handler]);
}
